// frontend/src/components/cart/Success.js - Updated without discounts/coupons

import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, ShoppingBag, MapPin, Phone, IndianRupee, Calendar, CreditCard } from 'lucide-react';
import MetaData from '../layouts/MetaData';

const Success = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const orderInfo = location.state?.orderInfo || location.state;

    useEffect(() => {
        if (!orderInfo) {
            navigate('/cart');
            return;
        }
    }, [navigate, orderInfo]);

    if (!orderInfo) return null;

    // Format the display of quantity based on the price type
    const formatQuantityDisplay = (item) => {
        if (item.priceType === 'perKg') {
            return `${item.quantity} kg`;
        } else if (item.priceType === 'perDozen') {
            return `${item.quantity} dozen (${item.quantity * 12} eggs)`;
        } else if (item.priceType === 'perPack') {
            return `${item.quantity} pack${item.quantity > 1 ? 's' : ''}`;
        } else if (item.priceType === 'fixed' && item.selectedWeight) {
            return `${item.quantity} × ${item.selectedWeight}`;
        }
        return `Quantity: ${item.quantity}`;
    };

    // Calculate individual item price without discounts
    const calculateItemPrice = (item) => {
        const basePrice = item.priceType === 'perDozen' ?
                         (item.dozenPrice || item.price) :
                         item.priceType === 'perPack' ?
                         (item.packPrice || item.price) :
                         item.price;
        
        return basePrice * item.quantity;
    };

    return (
        <>
            <MetaData title={'Order Success'} />
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <div className="max-w-2xl w-full space-y-8">
                    <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                        {/* Success Icon */}
                        <div className="flex flex-col items-center mb-8">
                            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle className="w-16 h-16 text-green-500" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 font-['Crimson_Text'] text-center">
                                Order Placed Successfully!
                            </h1>
                            <p className="text-gray-500 mt-2 text-center">
                                Thank you for shopping with us
                            </p>
                        </div>

                        {/* Order Information */}
                        <div className="space-y-6">
                            {/* Order Details */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    Order Details
                                </h2>
                                <div className="grid gap-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 flex items-center">
                                            <ShoppingBag className="w-4 h-4 mr-2" />
                                            Order ID:
                                        </span>
                                        <span className="font-medium">{orderInfo.orderId}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 flex items-center">
                                            <Calendar className="w-4 h-4 mr-2" />
                                            Order Date:
                                        </span>
                                        <span className="font-medium">
                                            {new Date(orderInfo.createdAt).toLocaleDateString('en-IN', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 flex items-center">
                                            <CreditCard className="w-4 h-4 mr-2" />
                                            Payment Method:
                                        </span>
                                        <span className="font-medium">
                                            {orderInfo.paymentInfo?.paymentMethod === 'COD'
                                                ? 'Cash on Delivery'
                                                : 'Online Payment'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 flex items-center">
                                            Status:
                                        </span>
                                        <span className="font-medium text-green-600">
                                            {orderInfo.orderStatus || 'Order Placed'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Shipping Information */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    Shipping Information
                                </h2>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm text-gray-600">Delivery Address:</p>
                                            <p className="text-sm font-medium">
                                                {orderInfo.shippingInfo?.address},
                                                {orderInfo.shippingInfo?.city} - {orderInfo.shippingInfo?.postalCode}
                                            </p>
                                            {orderInfo.shippingInfo?.areaName && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Area: {orderInfo.shippingInfo.areaName}
                                                    {orderInfo.shippingInfo.hubPlace && ` | Hub: ${orderInfo.shippingInfo.hubPlace}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm text-gray-600">Contact Number:</p>
                                            <p className="text-sm font-medium">{orderInfo.shippingInfo?.phoneno || orderInfo.shippingInfo?.phoneNo}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    Order Summary
                                </h2>
                                <div className="space-y-4">
                                    {orderInfo.orderItems?.map((item, index) => (
                                        <div key={index} className="flex justify-between items-center">
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-16 h-16 object-cover rounded-lg"
                                                />
                                                <div>
                                                    <p className="font-medium">{item.name}</p>
                                                    <p className="text-sm text-gray-600">
                                                        {formatQuantityDisplay(item)}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        Price: ₹{item.priceType === 'perDozen' ?
                                                            (item.dozenPrice || item.price).toFixed(2) :
                                                            item.priceType === 'perPack' ?
                                                            (item.packPrice || item.price).toFixed(2) :
                                                            item.price.toFixed(2)}
                                                        {item.priceType === 'perKg' && '/kg'}
                                                        {item.priceType === 'perDozen' && '/dozen'}
                                                        {item.priceType === 'perPack' && '/pack'}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="font-medium">
                                                ₹{calculateItemPrice(item).toFixed(2)}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Price Summary */}
                                <div className="border-t mt-4 pt-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Items Total:</span>
                                        <span>₹{orderInfo.itemsPrice?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Delivery Charge:</span>
                                        <span>₹{orderInfo.shippingPrice?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-medium text-lg pt-2 border-t">
                                        <span>Total Amount:</span>
                                        <div className="flex items-center text-[#dc2626]">
                                            <IndianRupee className="w-5 h-5 mr-1" />
                                            {orderInfo.totalPrice?.toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Timeline */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                    Delivery Information
                                </h2>
                                <p className="text-sm text-gray-800">
                                    Your order is being processed and will be delivered soon.
                                    {orderInfo.paymentInfo?.paymentMethod === 'COD' ?
                                        ' Please keep exact change ready for the delivery person.' :
                                        ' Thank you for your payment.'}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">
                                    Estimated delivery time: <span className="font-semibold">Within 1 hour</span>
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                    Delivery charge: <span className="font-semibold">₹50 (Fixed for all orders)</span>
                                </p>
                            </div>

                            {/* Action Button */}
                            <Link
                                to="/shop"
                                className="w-full flex items-center justify-center px-4 py-3 bg-[#dc2626]
                                    text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors"
                            >
                                <ShoppingBag className="w-5 h-5 mr-2" />
                                Continue Shopping
                            </Link>

                            {/* Additional Info */}
                            <div className="text-center text-sm text-gray-600">
                                <p>A confirmation email has been sent to your email address.</p>
                                <p className="mt-1">
                                    For any queries, contact us at <span className="font-medium">customersupport@meatbuddy.in</span> or call <span className="font-medium">+91 6364749404</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Success;